import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { LogIn, LogOut, FileX } from "lucide-react";

/**
 * Top-bar daily status pills.
 *  • After 09:30 IST → if NO check_in, show "Punch-in Pending" (red)
 *  • Else if after 17:30 IST → if check_in but NO check_out, show "Punch-out Pending" (amber)
 *  • If at least one punch is done → if daily report is NOT submitted, show "Report Not Submitted" (rose)
 *
 * Pills auto-refresh every 60s.
 */
export default function DailyStatusPills() {
  const [today, setToday] = useState(null);     // /punch/today
  const [report, setReport] = useState(null);   // /my-daily-report-status
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const [a, b] = await Promise.allSettled([
        api.get("/punch/today"),
        api.get("/my-daily-report-status"),
      ]);
      if (a.status === "fulfilled") setToday(a.value.data);
      if (b.status === "fulfilled") setReport(b.value.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tick]);

  // Compute current IST time as minutes-from-midnight
  const istNow = nowIST();
  const ist930 = 9 * 60 + 30;
  const ist1730 = 17 * 60 + 30;

  const hasCheckIn = !!today?.check_in;
  const hasCheckOut = !!today?.check_out;
  const isOnLeave = !!today?.on_leave;
  const reportSubmitted = !!report?.submitted;

  // If user is on leave today, don't nag.
  if (isOnLeave) return null;

  const pills = [];
  if (istNow >= ist930 && !hasCheckIn) {
    pills.push({
      key: "in",
      label: "Punch-in Pending",
      tone: "rose",
      icon: <LogIn size={11} />,
    });
  } else if (istNow >= ist1730 && hasCheckIn && !hasCheckOut) {
    pills.push({
      key: "out",
      label: "Punch-out Pending",
      tone: "amber",
      icon: <LogOut size={11} />,
    });
  }

  if ((hasCheckIn || hasCheckOut) && !reportSubmitted && istNow >= ist1730) {
    pills.push({
      key: "report",
      label: "Report Not Submitted",
      tone: "violet",
      icon: <FileX size={11} />,
      link: "/m/hr_daily_report",
    });
  }

  if (pills.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="daily-status-pills">
      {pills.map((p) => {
        const { key, ...rest } = p;
        return <Pill key={key} {...rest} />;
      })}
    </div>
  );
}

function Pill({ label, tone, icon, link }) {
  const cls = {
    rose: "bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100",
    amber: "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100",
    violet: "bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100",
  }[tone] || "bg-slate-50 border-slate-300 text-slate-800";
  const body = (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all ${cls}`}
      data-testid={`daily-status-${label.replace(/[^A-Za-z]/g, "_").toLowerCase()}`}
    >
      <span className="animate-pulse">●</span> {icon} {label}
    </span>
  );
  if (link) return <Link to={link}>{body}</Link>;
  return body;
}

function nowIST() {
  const fmt = new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
  const parts = fmt.formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
  const m = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
  return h * 60 + m;
}
